/* Customer portal — Account: balance, ageing, credit info. */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Wallet, TrendingUp, AlertTriangle } from "lucide-react";

export default function PortalAccount() {
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try { const r = await api.get("/portal/statement"); setData(r.data); } catch (e) { console.warn("portal statement load failed", e); }
    })();
  }, []);

  if (!data) return <div className="text-neutral-500">Loading account…</div>;

  const buckets = [
    { label: "Current", value: data.ageing.current,     tone: "good" },
    { label: "30 days", value: data.ageing.d30,         tone: "info" },
    { label: "60 days", value: data.ageing.d60,         tone: "warn" },
    { label: "90+ days", value: data.ageing.d90_plus,   tone: "critical" },
  ];
  const toneMap = {
    good: "border-emerald-500 bg-emerald-50 text-emerald-700",
    info: "border-neutral-400 bg-neutral-50 text-neutral-700",
    warn: "border-amber-500 bg-amber-50 text-amber-700",
    critical: "border-red-500 bg-red-50 text-red-700",
  };

  return (
    <div className="space-y-8" data-testid="portal-account">
      <div>
        <div className="text-[10px] tracking-[0.28em] uppercase text-neutral-500">Account</div>
        <h1 className="text-4xl font-black">{data.customer.name}</h1>
        <div className="text-neutral-500 text-sm mt-1 uppercase mono tracking-widest">{data.customer.customer_type} · {data.customer.credit_terms_days} day terms</div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-primary-orange text-black p-6 shadow-lg">
          <div className="text-[10px] tracking-widest uppercase opacity-70">Balance owing</div>
          <div className="mono text-4xl font-black mt-1" data-testid="portal-account-balance">${data.balance_owing.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
          <div className="text-xs opacity-80 mt-2 flex items-center gap-1"><Wallet className="w-3.5 h-3.5" /> Across {data.open_invoices?.length || 0} open invoice(s)</div>
        </div>
        <div className="bg-white border border-neutral-200 p-6 shadow-sm">
          <div className="text-[10px] tracking-widest uppercase text-neutral-500">Lifetime paid</div>
          <div className="mono text-4xl font-black mt-1">${data.total_paid_lifetime.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
          <div className="text-xs text-neutral-500 mt-2 flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" /> Thanks for your business</div>
        </div>
        <div className="bg-white border border-neutral-200 p-6 shadow-sm">
          <div className="text-[10px] tracking-widest uppercase text-neutral-500">Credit limit</div>
          <div className="mono text-4xl font-black mt-1">${(data.customer.credit_limit || 0).toLocaleString()}</div>
          <div className="text-xs text-neutral-500 mt-2">Get in touch to request an increase.</div>
        </div>
      </div>

      <div>
        <h2 className="text-[11px] uppercase tracking-widest text-neutral-500 mb-3">Ageing</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {buckets.map((b) => (
            <div key={b.label} data-testid={`ageing-${b.label.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`} className={`p-4 border-l-4 ${toneMap[b.tone]}`}>
              <div className="text-[10px] uppercase tracking-widest opacity-70">{b.label}</div>
              <div className="mono text-2xl font-black mt-1">${b.value.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h2 className="text-[11px] uppercase tracking-widest text-neutral-500 mb-3">Open invoices</h2>
        <div className="bg-white border border-neutral-200 divide-y divide-neutral-100">
          {(data.open_invoices || []).length === 0 && <div className="p-6 text-neutral-500 text-sm text-center">All paid — thank you.</div>}
          {(data.open_invoices || []).map((inv) => (
            <div key={inv.id} className="p-4 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="font-bold">{inv.invoice_no}</div>
                <div className="text-xs text-neutral-500">Issued {(inv.issued_at || "").slice(0, 10)} · {inv.status.toUpperCase()}</div>
              </div>
              <div className="mono font-black">${Number(inv.total).toFixed(2)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
